/**
 * 001-catalogue-admin-screens T064 (discount-rules-wizard). Page shell copied from the same source
 * `SkuUnits.tsx` cites: vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/category/create-category.tsx
 * (PageHeader + back-link) — the `DiscountRuleBuilder` (T062) fed by `ScopePicker`'s data (T063) is
 * the only form on the page.
 *
 * Root-loader gating note: there is no `/tiers/:tierId/rules` entry in `lib/nav.tsx` — the nav's
 * own `/tiers` prefix entry (`tiers.read`) already covers this path for `permissionForPath`
 * (`nav.tsx`'s own "longest matching href wins" comment), and this page does its own `can()` check
 * for the write half, the same split `SkuUnits.tsx` uses between viewing and editing a set.
 */
import { useState } from "react";
import { Link, useLoaderData, useNavigate, useOutletContext, useSearchParams, type LoaderFunctionArgs } from "react-router-dom";
import { useGoBack } from "../../lib/navigation";
import { tierPath } from "../../lib/paths";
import toast from "react-hot-toast";
import { PiInfoDuotone as InfoIcon } from "react-icons/pi";
import { Alert } from "rizzui";
import { PageHeader, ErrorSurface } from "@steelyard/theme";
import {
  byKey,
  getTier,
  getTierBySlug,
  getTierRules,
  activeOrKept,
  listBrands,
  listCategoryTree,
  setTierRules,
  type DiscountRuleInput,
} from "../../lib/services/catalog";
import { DiscountRuleBuilder } from "../../components/discount-rule-builder";
import { PricingFlow } from "../../components/pricing-flow";
import { ApiError } from "../../lib/api";
import { can } from "../../lib/permissions";
import type { AccountSummary, PermissionState } from "../../types";

/**
 * Active brands and categories only (project owner, 2026-09-29), plus any an existing rule already
 * targets: a rule can point at a now-inactive brand/category, and dropping it here would hide a live
 * rule's own target from the picker that is supposed to let an admin fix it.
 */
export async function tierRulesLoader({ params, request }: LoaderFunctionArgs) {
  const [tier, brands, categories] = await Promise.all([byKey(request, params.slug!, getTier, getTierBySlug, tierPath), listBrands(1, false, 200), listCategoryTree()]);
  const tierId = tier.id;
  const rules = await getTierRules(tierId);
  if (!rules || !brands || !categories) throw new Error("The API returned no body for this tier's rules.");
  const targets = rules.items.map((rule) => rule.scopeId);
  return { tierId, tier, rules, brands: activeOrKept(brands, targets), categories: activeOrKept(categories, targets) };
}

export default function TierRules() {
  const { tierId, tier, rules, brands, categories } = useLoaderData() as Awaited<ReturnType<typeof tierRulesLoader>>;
  const tierHref = tierPath(tier.slug);
  const goBack = useGoBack(tierHref);
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | undefined>();
  const navigate = useNavigate();
  // `?starter=` — a just-created tier's starting discount (New.tsx), offered once as an unsaved rule.
  const [searchParams] = useSearchParams();
  const starter = Number(searchParams.get("starter"));
  const starterPct = Number.isFinite(starter) && starter > 0 && starter <= 100 && rules.items.length === 0 ? starter : undefined;

  if (!can(permissionState, "pricing.read")) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403 }} action="view this tier's rules" />;
  }
  const canWrite = can(permissionState, "pricing.administer");

  async function handleSave(input: DiscountRuleInput[]) {
    setSaveError(undefined);
    setBusy(true);
    try {
      const result = await setTierRules(tierId, input);
      // Saving changes no price (FR-030), so the flow moves on to step 3 — the tier's prices, where
      // Generate prices applies the rules. The API's own counts travel in the toast.
      toast.success(`${result.rulesSaved} rule(s) saved — ${result.rowsFlaggedStale} price(s) now stale. Generate prices to apply them.`, {
        duration: 6000,
      });
      navigate(`${tierHref}/prices`);
    } catch (err) {
      setSaveError(err instanceof ApiError ? (err.detail ?? err.title ?? "The service refused this rule set.") : "The service refused this rule set.");
      toast.error("Could not save the discount rules");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-6 @container">
      <PageHeader
        title={`${tier.name} — Discount Rules`}
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Pricing · Tiers", href: "/tiers" }, { name: tier.name, href: tierHref }, { name: "Rules" }]}
      />

      <PricingFlow current="rules" tierSlug={tier.slug} />

      {/* How a rule is chosen, in the engine's own terms (PricingEngine.Resolve, FR-021/FR-022), and
          what saving does and does not do (FR-030) — 2026-09-29, the owner found this hard to follow. */}
      <Alert color="info" variant="flat" icon={<InfoIcon aria-hidden className="size-5" />}>
        <ul className="list-disc space-y-1 ps-4 text-sm">
          <li>
            A rule takes a percentage off the <strong>base price</strong> of everything it covers — all products, a brand, a
            category (with everything under it), a sub-category, or a single SKU.
          </li>
          <li>
            When several rules cover one product, the <strong>most specific</strong> wins: a SKU beats a sub-category, which beats
            its category; a brand and a top-level category rank the same. If two rules tie, the one <strong>higher in this list</strong>{" "}
            wins — use the arrows to reorder.
          </li>
          <li>A product no rule covers sells at its base price on this tier.</li>
          <li>
            Saving changes no price by itself. It marks the affected prices <strong>stale</strong>; they change when the tier&apos;s
            prices are generated or re-applied.
          </li>
          <li>
            No rule reaches a price <strong>set by hand</strong>. To put one back under these rules, use <strong>Revert to rule</strong>{" "}
            on its row in this tier&apos;s{" "}
            <Link to={`${tierHref}/prices`} className="underline">
              prices
            </Link>
            .
          </li>
        </ul>
      </Alert>

      {starterPct ? (
        <Alert color="warning" variant="flat">
          A starting &ldquo;All products&rdquo; rule at {starterPct}% is filled in below from the new tier&apos;s starting discount. It is not
          saved yet — press <strong>Save rules</strong> to keep it, or change it first.
        </Alert>
      ) : null}

      {canWrite ? (
        <DiscountRuleBuilder
          initialRules={rules.items}
          starterPct={starterPct}
          brands={brands.items}
          categories={categories.items}
          onSave={handleSave}
          onCancel={goBack}
          busy={busy}
          error={saveError}
        />
      ) : (
        <ErrorSurface error={{ title: "Not permitted", status: 403 }} action="edit this tier's discount rules" />
      )}
    </div>
  );
}
